import { gql } from 'graphql-tag';

import { FishingMethodFragments } from './FishingMethodType';
import { FishingWaterFragments } from './FishingWaterType';
import { ImageFragments } from './ImageType';
import { ProductFragments } from './ProductType';
import { ProductUnitFragments } from './ProductUnitType';
import { SpeciesFragments } from './SpeciesTypes';
import { UserFragments } from './UserType';
import { WeatherAndMarineReadingFragments } from './WeatherAndMarineReadingType';
import { WindDirectionFragments } from './WindDirectionType';

const catchId = gql`
  fragment CatchId on Catch {
    _id: externalId
  }
`;

const catchFishingWaterName = gql`
  fragment CatchFishingWaterName on Catch {
    fishingWater {
      ...FishingWaterId
      displayName
      latitude
      longitude
    }
  }
  ${FishingWaterFragments.id}
`;

const catchSpeciesName = gql`
  fragment CatchSpeciesName on Catch {
    species {
      ...SpeciesId
      displayName
    }
  }
  ${SpeciesFragments.id}
`;

/**
 * Note: SpeciesCatchWithImageNodeFields depends on PostFragments.id (PostId).
 * To avoid circular imports (PostType imports CatchType), PostId is NOT included
 * here. The query that uses this fragment must include PostFragments.id.
 */
const speciesCatchWithImageNodeFields = gql`
  fragment SpeciesCatchWithImageNodeFields on Catch {
    ...CatchId
    post {
      ...PostId
      catch {
        ...CatchId
        ...CatchFishingWaterName
        ...CatchSpeciesName
        caughtAtGmt
        weight
        length
      }
      createdAt
      displayProductUnits {
        totalCount
      }
      images(first: 1, croppingStrategy: SMART, height: 400, width: 400) {
        totalCount
        edges {
          node {
            ...ImageFields
          }
        }
      }
      user {
        ...UserId
        nickname
      }
    }
  }
  ${catchId}
  ${catchFishingWaterName}
  ${catchSpeciesName}
  ${ImageFragments.imageFields}
  ${UserFragments.id}
`;

const catchMetadata = gql`
  fragment CatchMetadata on Catch {
    __typename
    _id: externalId
    legacyId: id
    createdAt
  }
`;

/**
 * Note: SpeciesExampleCatchFields depends on PostFragments.id (PostId),
 * PostFragments.productUnits (PostProductUnits), and PostFragments.videoFields
 * (PostVideoFields). To avoid circular imports (PostType imports CatchType),
 * those dependencies are NOT included here. The query that uses this fragment
 * must include them.
 */
const speciesExampleCatchFields = gql`
  fragment SpeciesExampleCatchFields on Catch {
    ...CatchMetadata
    caughtAtGmt
    species {
      ...SpeciesId
      displayName
    }
    post {
      ...PostId
      createdAt
      displayProductUnits {
        totalCount
      }
      catch {
        ...CatchId
        ...CatchFishingWaterName
        ...CatchSpeciesName
      }
      comments {
        totalCount
      }
      ...PostProductUnits
      ...PostVideoFields
      images(first: 1, croppingStrategy: SMART, height: 400, width: 400) {
        totalCount
        edges {
          node {
            ...ImageFields
          }
        }
      }
      webPImages: images(first: 1, croppingStrategy: SMART, height: 400, width: 400, format: WEBP) {
        edges {
          node {
            ...ImageFields
          }
        }
      }
      likedByCurrentUser
      likesCount
      text {
        text
      }
      user {
        ...UserAvatar
        ...UserId
        nickname
        isPremium
        createdAt
      }
    }
  }
  ${catchMetadata}
  ${catchId}
  ${catchFishingWaterName}
  ${catchSpeciesName}
  ${SpeciesFragments.id}
  ${ImageFragments.imageFields}
  ${UserFragments.id}
  ${UserFragments.avatar}
`;

/**
 * Note: CountryTopWaterRecentCatchNodeFields depends on PostFragments.id (PostId),
 * FishingWaterFragments.id (FishingWaterId), SpeciesFragments.id (SpeciesId),
 * UserFragments.id (UserId), and ImageFragments.imageFields (ImageFields).
 * To avoid circular imports (PostType imports CatchType), PostId is NOT included
 * here. The query that uses this fragment must include PostFragments.id.
 */
const countryTopWaterRecentCatchNodeFields = gql`
  fragment CountryTopWaterRecentCatchNodeFields on Catch {
    ...CatchId
    post {
      ...PostId
      createdAt
      catch {
        ...CatchId
        caughtAtGmt
        fishingWater {
          ...FishingWaterId
          displayName
        }
        species {
          ...SpeciesId
          displayName
        }
        length
        weight
      }
      user {
        ...UserId
        nickname
      }
      displayProductUnits {
        totalCount
      }
      images(first: 1, croppingStrategy: SMART, height: 400, width: 400) {
        totalCount
        edges {
          node {
            ...ImageFields
          }
        }
      }
    }
  }
  ${catchId}
  ${FishingWaterFragments.id}
  ${SpeciesFragments.id}
  ${ImageFragments.imageFields}
  ${UserFragments.id}
`;

export const CatchFragments = {
  id: catchId,
  countryTopWaterRecentCatchNodeFields,
  speciesCatchWithImageNodeFields,
  speciesExampleCatchFields,
  location: gql`
    fragment CatchLocation on Catch {
      latitude
      longitude
    }
  `,
  metadata: catchMetadata,
  productUnits: gql`
    fragment CatchProductUnits on Catch {
      __typename
      productUnits(first: 20) {
        edges {
          node {
            ...ProductUnitId
            model
            image {
              ...ImageFields
            }
            product {
              ...ProductId
              ...ProductBrandName
              displayName
            }
          }
        }
      }
    }
    ${ImageFragments.imageFields}
    ${ProductFragments.brandName}
    ${ProductFragments.id}
    ${ProductUnitFragments.id}
  `,
  fishingWaterName: catchFishingWaterName,
  fishingMethodName: gql`
    fragment CatchFishingMethodName on Catch {
      fishingMethod {
        ...FishingMethodId
        displayName
      }
    }
    ${FishingMethodFragments.id}
  `,
  speciesName: catchSpeciesName,
  weatherFields: gql`
    fragment CatchWeatherFields on Catch {
      weatherAndMarineReading {
        ...WeatherAndMarineReadingId
        __typename
        airPressure
        airHumidity
        airTemperature
        waterTemperature
        weatherCondition {
          localizedValue
          worldWeatherOnlineIdentifier
        }
        windDirection {
          ...WindDirectionId
          __typename
          localizedValue
          shortLocalizedValue
          degrees
        }
        windSpeed
      }
    }
    ${WeatherAndMarineReadingFragments.id}
    ${WindDirectionFragments.id}
  `,
};
